import React, {useState, useEffect} from 'react';
import Highlighter from 'react-syntax-highlighter';
import style from 'react-syntax-highlighter/dist/cjs/styles/hljs/xcode';

import CopyButton from './CopyButton';
import DropdownInput from './DropdownInput';

const DEFAULT_LANGUAGE = 'plaintext';
const LAST_USED_LANGUAGE = 'last_used_language';

const getFormattedLanguageName = (lang = '') => {
  const languages = {
    sql: {label: 'SQL', syntax: 'sql'},
    postgres: {label: 'PostgreSQL', syntax: 'sql'},
    mysql: {label: 'MySQL', syntax: 'sql'},
    sqlite: {label: 'SQLite', syntax: 'sql'},
    js: {label: 'JavaScript', syntax: 'javascript'},
    javascript: {label: 'JavaScript', syntax: 'javascript'},
    csharp: {label: 'C#', syntax: 'csharp'},
    swift: {label: 'Swift', syntax: 'swift'},
    java: {label: 'Java', syntax: 'java'},
    cpp: {label: 'C++', syntax: 'cpp'},
    c: {label: 'C', syntax: 'c'},
    go: {label: 'Go', syntax: 'go'},
    ruby: {label: 'Ruby', syntax: 'ruby'},
    python: {label: 'Python', syntax: 'python'},
    pseudocode: {label: 'Pseudocode', syntax: 'plaintext'},
  };

  return languages[lang] || {label: '', syntax: 'plaintext'};
};

export const getLanguageFromClass = (className) => {
  const match = /language-(\w+)/.exec(className || '');
  const languageDetails = getFormattedLanguageName(match?.[1]);
  return {value: match?.[1] || '', ...languageDetails};
};

/** Pre wrapper that passes `inPre` prop to children for improved formatting */
export const Pre = ({children, ...props}) => {
  const childrenWithProps = React.Children
    .map(children, (child) => React.cloneElement(child, {inPre: true}));

  return (
    <pre {...props}>
      {childrenWithProps}
    </pre>
  );
};

export const CodeBlock = ({
  language, label, children, ...props
}) => (
  <div className="bg-white rounded-xl border border-gray-200 group my-5">
    {language ? (
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between text-sm font-medium text-gray-600">
        <span>{label}</span>
        <CopyButton value={children} className="invisible group-hover:visible" />
      </div>
    ) : (
      <div className="px-4 pt-1 flex relative text-sm font-medium text-gray-600">
        <CopyButton value={children} className="absolute top-2 right-2 invisible group-hover:visible" />
      </div>
    )}
    <div className="px-4 py-2">
      <Highlighter
        language={language || DEFAULT_LANGUAGE}
        PreTag="div"
        style={style}
        customStyle={{border: 'none', backgroundColor: 'transparent', padding: 0}}
        codeTagProps={{style: {backgroundColor: 'transparent', padding: 0}}}
        {...props}
      >
        {children}
      </Highlighter>
    </div>
  </div>
);

/**
 * Version of SyntaxHighlighter that can be used with react-markdown,
 * which doesnt wrap code blocks in <pre>, but passes an inline prop
 */
export const SyntaxHighlighter = ({
  className,
  inline,
  children,
  ...props
}) => {
  // Strip out trailing newline from input if necessary
  const cleanedChildren = String(children).trim();

  // Detect language syntax type
  const language = getLanguageFromClass(className);

  // Return either syntax-highlighted code block or inline code
  return (!inline)
    ? (
      <CodeBlock
        language={language.syntax}
        label={language.label}
        {...props}
      >
        {cleanedChildren}
      </CodeBlock>
    )
    : <code {...props}>{cleanedChildren}</code>;
};

/**
 * Version of SyntaxHighlighter that can be used with @mdx-js/mdx,
 * in which the only way to identify code blocks is that they are
 * wrapped in <pre>
 */
export const MDXSyntaxHighlighter = ({
  className,
  inPre,
  children,
  ...props
}) => {
  // Strip out trailing newline from input if necessary
  const cleanedChildren = String(children).trim();

  // Detect language syntax type
  const language = getLanguageFromClass(className);

  // Return either syntax-highlighted code block or inline code
  return (inPre)
    ? (
      <CodeBlock
        language={language.syntax}
        label={language.label}
        {...props}
      >
        {cleanedChildren}
      </CodeBlock>
    )
    : <code {...props}>{cleanedChildren}</code>;
};

export const CodeSwitcher = ({children}) => {
  const [activeLanguage, setActiveLanguage] = useState('');
  const [languageOptions, setLanguageOptions] = useState([]);

  useEffect(() => {
    const extractLanguage = (child) => {
      const {children: c} = child.props;
      const className = c?.props?.className;
      const content = c?.props?.children;

      const language = getLanguageFromClass(className);
      const trimmedContent = String(content ?? '').trim();

      return {...language, content: trimmedContent};
    };
    const languages = React.Children.toArray(children)
      .map((child) => extractLanguage(child));

    // Decide which language to display code in
    const lastUsedLanguage = localStorage.getItem(LAST_USED_LANGUAGE);
    if (lastUsedLanguage && languages.find(({value}) => value === lastUsedLanguage)) {
      setActiveLanguage(lastUsedLanguage);
    } else {
      setActiveLanguage(languages[0].value);
    }
    setLanguageOptions(languages);
  }, [children]);

  const changeLanguage = (value) => {
    setActiveLanguage(value);
    if (value) localStorage.setItem(LAST_USED_LANGUAGE, value);
  };

  const filteredLanguage = languageOptions.find((lang) => lang.value === activeLanguage);

  return (
    <div className="bg-white rounded-xl border border-gray-200 group my-5">
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between text-sm font-medium text-gray-600">
        <DropdownInput
          options={languageOptions}
          value={activeLanguage}
          onChange={changeLanguage}
          showReset={false}
          highlightActive={false}
          native
        />
      </div>
      <div className="px-4 pt-1 flex relative text-sm font-medium text-gray-600">
        <CopyButton value={filteredLanguage?.content ?? ''} className="absolute top-2 right-2 invisible group-hover:visible" />
      </div>
      <div className="px-4 py-2">
        <Highlighter
          language={filteredLanguage?.syntax}
          PreTag="div"
          customStyle={{border: 'none', backgroundColor: 'transparent', padding: 0}}
          codeTagProps={{style: {backgroundColor: 'transparent', padding: 0}}}
          style={style}
        >
          {filteredLanguage?.content ?? ''}
        </Highlighter>
      </div>
    </div>
  );
};
